import { useEffect, useState } from "react";
import { Link, NavLink, useLocation, useNavigate } from "react-router-dom";
import { navLinks, siteMeta } from "@/mocks/site";

export default function SiteHeader() {
  const [scrolled, setScrolled] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const [searchOpen, setSearchOpen] = useState(false);
  const [keyword, setKeyword] = useState("");
  const location = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    window.addEventListener("scroll", onScroll, { passive: true });
    onScroll();
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    setMenuOpen(false);
    setSearchOpen(false);
  }, [location.pathname, location.search]);

  useEffect(() => {
    document.body.style.overflow = menuOpen || searchOpen ? "hidden" : "";
    return () => {
      document.body.style.overflow = "";
    };
  }, [menuOpen, searchOpen]);

  const submitSearch = (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const q = keyword.trim();
    navigate(q ? `/search?q=${encodeURIComponent(q)}` : "/search");
    setSearchOpen(false);
    setKeyword("");
  };

  return (
    <>
      <header
        className={`sticky top-0 z-50 w-full border-b transition-colors duration-300 ${
          scrolled
            ? "border-background-200 bg-background-50/95 backdrop-blur-md"
            : "border-transparent bg-background-50"
        }`}
      >
        <div className="container-page flex h-16 items-center justify-between gap-6 md:h-[72px]">
          <Link to="/" className="flex items-center gap-2.5 whitespace-nowrap">
            <span className="flex h-9 w-9 items-center justify-center rounded-md bg-primary-900 text-accent-300">
              <i className="ri-movie-2-line text-base" />
            </span>
            <span className="flex flex-col leading-none">
              <span className="font-heading text-[17px] font-semibold tracking-tight text-foreground-950">
                {siteMeta.brand}
              </span>
              <span className="mt-1 font-label text-[9px] tracking-[0.28em] text-foreground-400">
                {siteMeta.tagline}
              </span>
            </span>
          </Link>

          <nav className="hidden items-center gap-1 md:flex" aria-label="主导航">
            {navLinks.map((link) => (
              <NavLink
                key={link.to}
                to={link.to}
                end={link.to === "/"}
                className={({ isActive }) =>
                  `rounded-md px-3.5 py-2 text-sm transition-colors ${
                    isActive
                      ? "bg-primary-100 font-medium text-primary-900"
                      : "text-foreground-700 hover:bg-background-100 hover:text-foreground-950"
                  }`
                }
              >
                {link.label}
              </NavLink>
            ))}
          </nav>

          <div className="flex items-center gap-2">
            <form onSubmit={submitSearch} className="hidden items-center lg:flex" role="search">
              <div className="flex items-center gap-2 rounded-full border border-background-300/80 bg-background-100/70 px-3 py-1.5 transition-colors focus-within:border-accent-400">
                <span className="flex h-4 w-4 items-center justify-center text-foreground-500">
                  <i className="ri-search-line text-sm" />
                </span>
                <input
                  type="search"
                  value={keyword}
                  onChange={(event) => setKeyword(event.target.value)}
                  placeholder="搜索剧名、导演、标签"
                  aria-label="站内搜索"
                  className="w-40 bg-transparent text-sm text-foreground-900 outline-none placeholder:text-foreground-400"
                />
              </div>
            </form>

            <button
              type="button"
              onClick={() => setSearchOpen(true)}
              aria-label="打开搜索"
              className="flex h-9 w-9 cursor-pointer items-center justify-center rounded-full border border-background-300/70 text-foreground-800 transition-colors hover:border-accent-400 hover:text-accent-700 lg:hidden"
            >
              <i className="ri-search-line" />
            </button>

            <button
              type="button"
              onClick={() => setMenuOpen(true)}
              aria-label="打开菜单"
              className="flex h-9 w-9 cursor-pointer items-center justify-center rounded-full border border-background-300/70 text-foreground-800 md:hidden"
            >
              <i className="ri-menu-3-line text-lg" />
            </button>
          </div>
        </div>
      </header>

      {menuOpen ? (
        <div className="fixed inset-0 z-[60] md:hidden">
          <button
            type="button"
            aria-label="关闭菜单"
            onClick={() => setMenuOpen(false)}
            className="absolute inset-0 cursor-pointer bg-primary-950/40"
          />
          <div className="absolute right-0 top-0 flex h-full w-[82%] max-w-xs flex-col bg-background-50 p-5 animate-fade-in">
            <div className="flex items-center justify-between">
              <span className="font-heading text-base font-semibold text-foreground-950">目录</span>
              <button
                type="button"
                onClick={() => setMenuOpen(false)}
                aria-label="关闭菜单"
                className="flex h-8 w-8 cursor-pointer items-center justify-center rounded-full bg-background-100 text-foreground-700"
              >
                <i className="ri-close-line text-lg" />
              </button>
            </div>
            <nav className="mt-6 flex flex-col" aria-label="移动端主导航">
              {navLinks.map((link) => (
                <NavLink
                  key={link.to}
                  to={link.to}
                  end={link.to === "/"}
                  className={({ isActive }) =>
                    `flex items-center justify-between border-b border-background-200 py-3.5 text-[15px] ${
                      isActive ? "font-medium text-primary-900" : "text-foreground-700"
                    }`
                  }
                >
                  {link.label}
                  <i className="ri-arrow-right-s-line text-foreground-400" />
                </NavLink>
              ))}
            </nav>
            <p className="mt-auto text-xs leading-relaxed text-foreground-500">{siteMeta.disclaimer}</p>
          </div>
        </div>
      ) : null}

      {searchOpen ? (
        <div className="fixed inset-0 z-[60] flex flex-col bg-background-50 animate-fade-in lg:hidden">
          <div className="container-page flex h-16 items-center justify-between">
            <span className="rule-label text-foreground-500">搜索</span>
            <button
              type="button"
              onClick={() => setSearchOpen(false)}
              aria-label="关闭搜索"
              className="flex h-9 w-9 cursor-pointer items-center justify-center rounded-full bg-background-100 text-foreground-700"
            >
              <i className="ri-close-line text-lg" />
            </button>
          </div>
          <div className="container-page pt-4">
            <form onSubmit={submitSearch} role="search">
              <label htmlFor="mobile-search" className="rule-label text-foreground-500">
                输入关键词
              </label>
              <div className="mt-2 flex items-center gap-2 border-b-2 border-primary-800 pb-2">
                <input
                  id="mobile-search"
                  type="search"
                  autoFocus
                  value={keyword}
                  onChange={(event) => setKeyword(event.target.value)}
                  placeholder="例如：家庭、昭和、职场"
                  className="w-full bg-transparent text-lg text-foreground-950 outline-none placeholder:text-foreground-300"
                />
                <button
                  type="submit"
                  className="flex h-9 w-9 shrink-0 cursor-pointer items-center justify-center rounded-full bg-accent-500 text-background-50"
                  aria-label="提交搜索"
                >
                  <i className="ri-arrow-right-line" />
                </button>
              </div>
            </form>
            <p className="mt-6 text-xs text-foreground-500">试试这些标签</p>
            <div className="mt-3 flex flex-wrap gap-2">
              {["家庭", "昭和", "职场伦理", "爱情", "悬疑"].map((tag) => (
                <button
                  key={tag}
                  type="button"
                  onClick={() => {
                    navigate(`/search?q=${encodeURIComponent(tag)}`);
                    setSearchOpen(false);
                  }}
                  className="cursor-pointer rounded-full border border-background-300 px-3 py-1.5 text-xs text-foreground-700"
                >
                  {tag}
                </button>
              ))}
            </div>
          </div>
        </div>
      ) : null}
    </>
  );
}